"use client";

import { useEffect, useState } from "react";
import { getProposals } from "@/features/enterprise-proposals-grid/_logic/actions";
import type { ProposalCard } from "@/features/enterprise-proposals-grid/_types/ui-types";

/** One row on the compare page: a venue, not an individual offer. */
export interface ComparableVenue {
  venueStaticId: number;
  venueName: string;
  location: string | null;
}

interface UseComparableProposalsResult {
  venues: ComparableVenue[];
  isLoading: boolean;
  error: string | null;
}

/** Enough to fill the four compare slots several times over without paging. */
const FETCH_LIMIT = 50;

/**
 * Loads this event's proposals as a venue list for the compare page.
 *
 * Calls `getProposals` directly: it is already a server action with its own
 * `checkEnterpriseEventAccess`, so the palette needs no new endpoint and cannot
 * see an event the user could not otherwise open.
 *
 * Deduped to one entry per venue. `getProposals` returns one card per *offer*,
 * and a venue holding three quotes would otherwise appear three times while the
 * compare page renders it as a single column.
 *
 * @param eventId - The event to load, or null to stay idle.
 * @param enabled - False while the compare page is not open, so opening the
 *   palette never fetches proposals the user did not ask for.
 * @returns The venue list, plus loading and error state.
 */
export function useComparableProposals(eventId: string | null, enabled: boolean): UseComparableProposalsResult {
  // States
  const [venues, setVenues] = useState<ComparableVenue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Effects
  useEffect(() => {
    if (!enabled || !eventId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getProposals({
      eventId: Number(eventId),
      tab: "received",
      offset: 0,
      search: "",
      sort: { field: "proposalDate", direction: "desc" },
      limit: FETCH_LIMIT,
    })
      .then((page) => {
        if (cancelled) return;
        const byVenue = new Map<number, ComparableVenue>();
        page.items.forEach((card: ProposalCard) => {
          if (byVenue.has(card.venueStaticId)) return;
          byVenue.set(card.venueStaticId, {
            venueStaticId: card.venueStaticId,
            venueName: card.venueName,
            location: [card.city, card.state ?? card.country].filter(Boolean).join(", ") || null,
          });
        });
        setVenues([...byVenue.values()]);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load proposals");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, eventId]);

  return { venues, isLoading, error };
}
